import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Loader2, AlertCircle } from 'lucide-react';

export default function OAuthCallbackPage() {
  const { provider } = useParams<{ provider: string }>();
  const { loginWithOAuth, setTokensAndFetchUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    async function handleCallback() {
      try {
        // Check for error in query params
        const params = new URLSearchParams(window.location.search);
        const errorParam = params.get('error');
        if (errorParam) {
          setError(params.get('error_description') || errorParam);
          return;
        }

        // Apple flow: tokens may be in URL fragment
        const fragment = new URLSearchParams(window.location.hash.slice(1));
        const fragmentAccessToken = fragment.get('access_token');

        if (fragmentAccessToken) {
          // Direct token from Apple redirect (backend already exchanged code)
          const refreshToken = fragment.get('refresh_token') || undefined;
          await setTokensAndFetchUser({
            accessToken: fragmentAccessToken,
            refreshToken,
          });
          navigate(getRedirect(), { replace: true });
          return;
        }

        // Standard OAuth code exchange (Google, etc.)
        const code = params.get('code');
        if (!code) {
          setError('No authorization code received');
          return;
        }

        const callbackUrl = `${window.location.origin}/auth/callback/${provider}`;
        const idToken = params.get('id_token') || undefined;
        const userInfo = params.get('user') || undefined;

        await loginWithOAuth(
          provider || 'google',
          code,
          callbackUrl,
          idToken || userInfo ? { id_token: idToken, user: userInfo } : undefined,
        );

        navigate(getRedirect(), { replace: true });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Authentication failed';
        setError(message);
      }
    }

    handleCallback();
  }, [provider, loginWithOAuth, setTokensAndFetchUser, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex flex-col items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <AlertCircle className="text-red-400" size={28} />
          </div>
          <h2 className="text-xl font-bold text-[#F5F7FF] mb-2">Authentication Failed</h2>
          <p className="text-sm text-[#A7B0C8] mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex h-10 px-6 items-center justify-center bg-[#A38BFF] text-white font-medium rounded-xl hover:bg-[#7B68EE] transition-colors"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D10] flex flex-col items-center justify-center">
      <Loader2 size={32} className="animate-spin text-[#A38BFF] mb-4" />
      <p className="text-sm text-[#A7B0C8]">Completing sign in...</p>
    </div>
  );
}

function getRedirect(): string {
  try {
    const stored = sessionStorage.getItem('auth_redirect');
    if (stored) {
      sessionStorage.removeItem('auth_redirect');
      return stored;
    }
  } catch {
    // ignore
  }
  return '/';
}
